import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { del, get, post } from '../../api/request';
import { API_BASE_URL, API_ENDPOINTS } from '../../api/config';
import './DownloadManagement.css';

const { TextArea } = Input;
const { Dragger } = Upload;

interface AppRelease {
  id: number;
  version: string;
  platform: string;
  file_name: string;
  object_key: string;
  download_url: string;
  file_size: number;
  content_type: string;
  notes: string;
  is_active: boolean;
  status: number;
  sort: number;
  created_at?: string;
  updated_at?: string;
}

const platformOptions = [
  { label: 'Android (apk)', value: 'android' },
  { label: 'iOS (ipa)', value: 'ios' },
];

const platformLabel: Record<string, string> = {
  android: 'Android',
  ios: 'iOS',
};

const formatSize = (bytes: number) => {
  if (!bytes) return '-';
  const mb = bytes / 1024 / 1024;
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
};

const DownloadManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [items, setItems] = useState<AppRelease[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await get<AppRelease[]>(API_ENDPOINTS.download.releases);
      setItems(data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载安装包列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleUpload = async () => {
    const file = fileList[0]?.originFileObj as File | undefined;
    if (!file) {
      message.warning('请先选择安装包文件');
      return;
    }
    const values = await form.validateFields();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('platform', values.platform);
    formData.append('version', values.version || '');
    formData.append('notes', values.notes || '');
    formData.append('set_active', values.set_active ? 'true' : 'false');

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.download.releaseUpload}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || `上传失败: ${response.status}`);
      }
      message.success('安装包上传成功');
      setFileList([]);
      form.resetFields();
      setModalOpen(false);
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '上传安装包失败');
    } finally {
      setUploading(false);
    }
  };

  const setActive = async (record: AppRelease) => {
    try {
      await post(API_ENDPOINTS.download.releaseActive(record.id));
      message.success(`已发布为 ${platformLabel[record.platform] || record.platform} 当前版本`);
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '设置失败');
    }
  };

  const deleteItem = async (record: AppRelease) => {
    try {
      await del(API_ENDPOINTS.download.releaseDetail(record.id));
      message.success('版本记录已删除');
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除安装包失败');
    }
  };

  const columns: ColumnsType<AppRelease> = [
    {
      title: '平台',
      dataIndex: 'platform',
      width: 110,
      render: (value) => <Tag color={value === 'android' ? 'green' : 'blue'}>{platformLabel[value] || value}</Tag>,
    },
    {
      title: '版本',
      dataIndex: 'version',
      width: 120,
      render: (value) => value || <span className="download-muted">未填写</span>,
    },
    {
      title: '文件',
      dataIndex: 'file_name',
      render: (value, record) => (
        <div className="download-file-cell">
          <strong>{value}</strong>
          <a href={record.download_url} target="_blank" rel="noreferrer">{record.download_url}</a>
        </div>
      ),
    },
    {
      title: '大小',
      dataIndex: 'file_size',
      width: 110,
      render: formatSize,
    },
    {
      title: '发布状态',
      dataIndex: 'is_active',
      width: 160,
      render: (value, record) =>
        value ? (
          <Tag icon={<CheckCircleOutlined />} color="success">当前发布</Tag>
        ) : (
          <Tag color="default">未发布</Tag>
        ),
    },
    {
      title: '记录状态',
      dataIndex: 'status',
      width: 110,
      render: (value) => value === 1 ? <Tag color="blue">可发布</Tag> : <Tag>已停用</Tag>,
    },
    {
      title: '备注',
      dataIndex: 'notes',
      width: 200,
      render: (value) => value || <span className="download-muted">-</span>,
    },
    {
      title: '操作',
      width: 170,
      render: (_, record) => (
        <div className="download-actions-cell">
          {!record.is_active && (
            <Popconfirm
              title={`发布该 ${platformLabel[record.platform] || record.platform} 版本？`}
              description="发布后，同平台其它版本会自动取消发布。"
              onConfirm={() => setActive(record)}
            >
              <Button size="small" type="primary" icon={<RocketOutlined />} disabled={record.status !== 1}>
                发布
              </Button>
            </Popconfirm>
          )}
          <Popconfirm title="确认删除该版本？将同时删除云存储文件" onConfirm={() => deleteItem(record)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const activeByPlatform = platformOptions.map((option) => {
    const active = items.find((item) => item.platform === option.value && item.is_active);
    return { ...option, active };
  });

  return (
    <div className="download-page">
      <div className="download-header">
        <div>
          <h1 className="page-title">产品发布</h1>
          <p>管理 App 安装包版本。新增版本后进入列表，点击「发布」才会成为落地页当前可下载版本；同一平台只能有一个当前发布版本。</p>
        </div>
        <div className="download-header-actions">
          <Button icon={<ReloadOutlined />} onClick={fetchItems}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>新增版本</Button>
        </div>
      </div>

      <div className="download-summary-grid">
        {activeByPlatform.map(({ label, value, active }) => (
          <Card bordered={false} className="download-summary-card" key={value}>
            <span>{label}</span>
            <strong>{active ? (active.version || active.file_name) : '暂无发布版本'}</strong>
            <p>{active ? `当前落地页可下载：${active.file_name}` : '新增版本后点击发布即可上线'}</p>
          </Card>
        ))}
      </div>

      <Card bordered={false} title="发布版本列表">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1180 }}
        />
      </Card>

      <Modal
        title="新增发布版本"
        open={modalOpen}
        onCancel={() => {
          if (!uploading) {
            setModalOpen(false);
            setFileList([]);
            form.resetFields();
          }
        }}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)} disabled={uploading}>取消</Button>,
          <Button
            key="submit"
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={uploading}
            onClick={handleUpload}
            disabled={fileList.length === 0}
          >
            新增版本
          </Button>,
        ]}
        width={860}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ platform: 'android', version: '', notes: '', set_active: false }}
        >
          <div className="download-form-grid">
            <Form.Item label="平台" name="platform" rules={[{ required: true, message: '请选择平台' }]}>
              <Select options={platformOptions} />
            </Form.Item>
            <Form.Item label="版本号" name="version">
              <Input placeholder="如 1.0.0，可留空" />
            </Form.Item>
            <Form.Item label="新增后立即发布" name="set_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <Form.Item label="备注" name="notes">
            <TextArea rows={2} placeholder="更新说明等，可留空" />
          </Form.Item>
          <Form.Item label="安装包文件">
            <Dragger
              fileList={fileList}
              maxCount={1}
              beforeUpload={(file) => {
                setFileList([{ uid: file.uid, name: file.name, originFileObj: file } as UploadFile]);
                return false;
              }}
              onRemove={() => setFileList([])}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">点击或拖拽安装包到此处</p>
              <p className="ant-upload-hint">支持 apk / ipa，单个文件不超过 1GB</p>
            </Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DownloadManagement;
